import React from 'react';
import { useNavigate } from 'react-router-dom';
import SalesAchievementListTable from '../components/SalesAchievementListTable';

const cardStyle = {
    backgroundColor: '#ffffff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '20px 24px'
};

const labelStyle = { fontSize: '0.8125rem', color: '#6c757d' };
const valueStyle = { fontSize: '1.75rem', fontWeight: 700, color: '#151f2c', lineHeight: 1.2 };
const subStyle = { fontSize: '0.8125rem', color: '#6c757d', marginTop: '0.75rem' };

const formatIdr = (value, { minFrac = 0, maxFrac = 0 } = {}) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return '0';
    return new Intl.NumberFormat('id-ID', {
        minimumFractionDigits: minFrac,
        maximumFractionDigits: maxFrac
    }).format(n);
};

const ContractWon = ({
    summary,
    salesAchievementData,
    achievementMetrics,
    filters,
    searchQuery,
    setSearchQuery,
    isLoading,
    handleExportExcel,
    formatCurrencyValue
}) => {
    const navigate = useNavigate();

    const handleProjectCountClick = (row) => {
        const params = new URLSearchParams();
        const period = filters?.period;
        if (period && period !== 'all') {
            params.append('stageUpdatePeriod', period);
        }
        // Prefer the clicked row's user; otherwise carry over the active Sales Creator filter.
        const assignedUserIds = row?.id
            ? [row.id]
            : (Array.isArray(filters?.creator) ? filters.creator : []);
        if (assignedUserIds.length > 0) {
            params.append('assignedTo', assignedUserIds.join(','));
        }
        params.append('proposalStage', '5');
        navigate(`/reports/proposals?${params.toString()}`);
    };

    const totalRevenue = Number(
        summary?.totalRevenue ?? achievementMetrics?.totalRevenue ?? 0
    );

    const targetRevenue = Number(
        summary?.targetRevenue ?? achievementMetrics?.targetRevenue ?? achievementMetrics?.target ?? 0
    );

    const wonProposalCount = Number(summary?.wonProposalCount ?? 0);

    const achievementRate = (() => {
        if (summary?.achievementRatePercent != null && summary.achievementRatePercent !== '') {
            return Number(summary.achievementRatePercent);
        }
        if (achievementMetrics?.achievement != null && achievementMetrics.achievement !== '') {
            return Number(achievementMetrics.achievement);
        }
        return targetRevenue > 0 ? (totalRevenue / targetRevenue) * 100 : 0;
    })();

    const averageValue =
        summary?.averageValuePerWonProposal != null && summary.averageValuePerWonProposal !== ''
            ? Number(summary.averageValuePerWonProposal)
            : wonProposalCount > 0
                ? totalRevenue / wonProposalCount
                : 0;


    return (
        <div>
            <h3 className="mb-3">Total Contract Won</h3>
            <div className="row g-3 mb-4">
                <div className="col-md-4">
                    <div style={cardStyle}>
                        <div style={labelStyle}>Total Contract Won</div>
                        <div className="mt-2" style={valueStyle}>
                            Rp{formatCurrencyValue(totalRevenue)}
                        </div>
                        <div
                            style={{ ...subStyle, cursor: 'pointer', color: '#0dcaf0', transition: 'opacity 0.2s' }}
                            onClick={() => handleProjectCountClick()}
                            onMouseEnter={(e) => { e.target.style.opacity = '0.7'; }}
                            onMouseLeave={(e) => { e.target.style.opacity = '1'; }}
                            title="Click to view all won proposals"
                        >
                            {wonProposalCount} Proposal Won
                        </div>
                    </div>
                </div>
                <div className="col-md-4">
                    <div style={cardStyle}>
                        <div style={labelStyle}>Achievement Rate</div>
                        <div className="mt-2" style={valueStyle}>
                            {Number.isFinite(achievementRate) ? achievementRate.toFixed(1) : '0.0'}%
                        </div>
                        <div style={subStyle}>
                            Achievement against target: Rp
                            {targetRevenue > 0
                                ? formatCurrencyValue(targetRevenue)
                                : formatCurrencyValue(0)}
                        </div>
                    </div>
                </div>
                <div className="col-md-4">
                    <div style={cardStyle}>
                        <div style={labelStyle}>Average Value</div>
                        <div className="mt-2" style={valueStyle}>
                            Rp{formatIdr(averageValue, { minFrac: 2, maxFrac: 2 })}
                        </div>
                        <div style={subStyle}>Per Proposal</div>
                    </div>
                </div>
            </div>

            <SalesAchievementListTable
                salesData={salesAchievementData}
                metrics={achievementMetrics}
                filters={filters}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                isLoading={isLoading}
                handleExportExcel={handleExportExcel}
                formatCurrencyValue={formatCurrencyValue}
                uniqueId="total-contract-won-list"
                countColumnLabel="PROPOSAL WON"
                onCountClick={handleProjectCountClick}
            />
        </div>
    );
};

export default ContractWon;
